import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
    useSettings,
    createSetting,
    updateSetting,
    deleteSetting as deleteSettingApi,
    Setting,
    SettingGroup,
    NewSetting,
    JsonField,
} from "../api/users";
import { useAuth } from "../hooks/useAuth";

const SettingsPage: React.FC = () => {
    const { t } = useTranslation("settings");
    const { user } = useAuth();

    const [selectedGroup, setSelectedGroup] = useState<string>("");
    const [editingSettings, setEditingSettings] = useState<Record<number, any>>(
        {}
    );
    const [showAddForm, setShowAddForm] = useState(false);
    const [newSetting, setNewSetting] = useState<NewSetting>({
        key: "",
        value: "",
        type: "string",
        description: "",
    });
    const [saving, setSaving] = useState(false);
    const [expandedJsonSettings, setExpandedJsonSettings] = useState<
        Record<number, boolean>
    >({});
    const [jsonFields, setJsonFields] = useState<Record<number, JsonField[]>>(
        {}
    );
    const [error, setError] = useState<string>("");

    const { data: settings, isLoading, refetch } = useSettings();

    // Проверяем, является ли пользователь администратором
    const isAdmin = user?.roles?.includes("admin") || false;

    // Функция для группировки настроек
    const getSettingGroups = (settingsList: Setting[]): SettingGroup[] => {
        const groups: Record<string, Setting[]> = {};

        if (!Array.isArray(settingsList)) {
            console.warn("settingsList is not an array:", settingsList);
            return [];
        }

        settingsList.forEach((setting) => {
            if (!setting || !setting.key) {
                console.warn("Invalid setting:", setting);
                return;
            }

            const groupName = setting.key.split(".")[0] || "general";
            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(setting);
        });

        return Object.entries(groups)
            .map(([name, settings]) => ({
                name,
                settings: settings.sort((a, b) => a.key.localeCompare(b.key)),
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    // Устанавливаем первую группу как активную при загрузке данных
    React.useEffect(() => {
        if (settings && settings.length > 0 && !selectedGroup) {
            const groups = getSettingGroups(settings);
            if (groups.length > 0) {
                setSelectedGroup(groups[0].name);
            }
        }
    }, [settings, selectedGroup]);

    const handleSettingChange = (settingId: number, value: any) => {
        setEditingSettings((prev) => ({
            ...prev,
            [settingId]: value,
        }));
    };

    // Функция для парсинга JSON и создания полей
    const parseJsonToFields = (jsonValue: any): JsonField[] => {
        if (typeof jsonValue === "string") {
            try {
                jsonValue = JSON.parse(jsonValue);
            } catch {
                return [];
            }
        }

        if (typeof jsonValue !== "object" || jsonValue === null) {
            return [];
        }

        const fields: JsonField[] = [];
        const processObject = (obj: any, prefix = "") => {
            Object.entries(obj).forEach(([key, value]) => {
                const fullKey = prefix ? `${prefix}.${key}` : key;

                if (
                    typeof value === "object" &&
                    value !== null &&
                    !Array.isArray(value)
                ) {
                    processObject(value, fullKey);
                } else {
                    let type: "string" | "number" | "boolean" = "string";
                    if (typeof value === "number") type = "number";
                    else if (typeof value === "boolean") type = "boolean";

                    fields.push({
                        key: fullKey,
                        value: value,
                        type,
                    });
                }
            });
        };

        processObject(jsonValue);
        return fields;
    };

    // Функция для сборки JSON из полей
    const buildJsonFromFields = (fields: JsonField[]): any => {
        const result: any = {};

        fields.forEach((field) => {
            const keys = field.key.split(".");
            let current = result;

            keys.forEach((key, index) => {
                if (index === keys.length - 1) {
                    // Последний ключ - устанавливаем значение
                    let value = field.value;
                    if (field.type === "number") value = Number(value);
                    else if (field.type === "boolean")
                        value = value === "true" || value === true;
                    current[key] = value;
                } else {
                    // Промежуточный ключ - создаем объект
                    if (!current[key]) current[key] = {};
                    current = current[key];
                }
            });
        });

        return result;
    };

    // Функция для обновления JSON-поля
    const updateJsonField = (
        settingId: number,
        fieldKey: string,
        value: any
    ) => {
        setJsonFields((prev) => {
            const currentFields = prev[settingId] || [];
            const updatedFields = currentFields.map((field) =>
                field.key === fieldKey ? { ...field, value } : field
            );
            return { ...prev, [settingId]: updatedFields };
        });
    };

    // Функция для добавления нового JSON-поля
    const addJsonField = (settingId: number) => {
        setJsonFields((prev) => {
            const currentFields = prev[settingId] || [];
            const newField: JsonField = {
                key: `new_field_${Date.now()}`,
                value: "",
                type: "string",
            };
            return { ...prev, [settingId]: [...currentFields, newField] };
        });
    };

    // Функция для удаления JSON-поля
    const removeJsonField = (settingId: number, fieldKey: string) => {
        setJsonFields((prev) => {
            const currentFields = prev[settingId] || [];
            const updatedFields = currentFields.filter(
                (field) => field.key !== fieldKey
            );
            return { ...prev, [settingId]: updatedFields };
        });
    };

    const saveSetting = useCallback(
        async (setting: Setting) => {
            try {
                setSaving(true);
                setError("");

                let updatedValue = editingSettings[setting.id];

                // Если это JSON и есть поля, собираем JSON из полей
                if (setting.type === "json" && jsonFields[setting.id]) {
                    updatedValue = buildJsonFromFields(jsonFields[setting.id]);
                }

                if (updatedValue === undefined) return;

                await updateSetting(setting.id, {
                    value: updatedValue,
                    type: setting.type,
                    description: setting.description,
                });

                // Обновляем локальное состояние
                await refetch();

                // Удаляем из editingSettings и jsonFields
                setEditingSettings((prev) => {
                    const newState = { ...prev };
                    delete newState[setting.id];
                    return newState;
                });

                setJsonFields((prev) => {
                    const newState = { ...prev };
                    delete newState[setting.id];
                    return newState;
                });

                setExpandedJsonSettings((prev) => {
                    const newState = { ...prev };
                    delete newState[setting.id];
                    return newState;
                });
            } catch (err: any) {
                console.error("Error saving setting:", err);
                setError(
                    "Ошибка сохранения настройки: " +
                        (err.message || "Неизвестная ошибка")
                );
            } finally {
                setSaving(false);
            }
        },
        [editingSettings, jsonFields, refetch]
    );

    const addNewSetting = useCallback(async () => {
        try {
            setSaving(true);
            setError("");

            await createSetting(newSetting);

            // Очищаем форму
            setNewSetting({
                key: "",
                value: "",
                type: "string",
                description: "",
            });
            setShowAddForm(false);

            // Обновляем список настроек
            await refetch();
        } catch (err: any) {
            console.error("Error adding setting:", err);
            setError(
                "Ошибка добавления настройки: " +
                    (err.message || "Неизвестная ошибка")
            );
        } finally {
            setSaving(false);
        }
    }, [newSetting, refetch]);

    const deleteSettingHandler = useCallback(
        async (setting: Setting) => {
            if (
                !window.confirm(
                    `Вы уверены, что хотите удалить настройку "${setting.key}"?`
                )
            ) {
                return;
            }

            try {
                setSaving(true);
                setError("");

                await deleteSettingApi(setting.id);

                // Обновляем список настроек
                await refetch();
            } catch (err: any) {
                console.error("Error deleting setting:", err);
                setError(
                    "Ошибка удаления настройки: " +
                        (err.message || "Неизвестная ошибка")
                );
            } finally {
                setSaving(false);
            }
        },
        [refetch]
    );

    const renderSettingValue = (setting: Setting) => {
        const value =
            editingSettings[setting.id] !== undefined
                ? editingSettings[setting.id]
                : setting.value;
        const isJsonExpanded = expandedJsonSettings[setting.id];
        const currentJsonFields = jsonFields[setting.id] || [];

        switch (setting.type) {
            case "bool":
                return (
                    <select
                        value={value ? "true" : "false"}
                        onChange={(e) =>
                            handleSettingChange(
                                setting.id,
                                e.target.value === "true"
                            )
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900"
                    >
                        <option value="true">{t("boolean_values.true")}</option>
                        <option value="false">
                            {t("boolean_values.false")}
                        </option>
                    </select>
                );

            case "int":
                return (
                    <input
                        type="number"
                        value={value}
                        onChange={(e) =>
                            handleSettingChange(
                                setting.id,
                                parseInt(e.target.value) || 0
                            )
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900"
                    />
                );

            case "json":
                return (
                    <div className="space-y-3">
                        {/* Кнопка для разворачивания/сворачивания JSON */}
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                onClick={() => {
                                    setExpandedJsonSettings((prev) => ({
                                        ...prev,
                                        [setting.id]: !isJsonExpanded,
                                    }));

                                    // Если разворачиваем и нет полей, парсим JSON
                                    if (
                                        !isJsonExpanded &&
                                        currentJsonFields.length === 0
                                    ) {
                                        const fields = parseJsonToFields(value);
                                        setJsonFields((prev) => ({
                                            ...prev,
                                            [setting.id]: fields,
                                        }));
                                    }
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                                {isJsonExpanded
                                    ? t("json_editor.collapse")
                                    : t("json_editor.expand")}
                            </button>

                            {isJsonExpanded && (
                                <button
                                    type="button"
                                    onClick={() => addJsonField(setting.id)}
                                    className="text-sm text-green-600 hover:text-green-800 font-medium"
                                >
                                    {t("json_editor.add_field")}
                                </button>
                            )}
                        </div>

                        {isJsonExpanded ? (
                            /* Развернутый режим - поля JSON */
                            <div className="space-y-3 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                                {currentJsonFields.length === 0 ? (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {t("json_editor.no_fields")}
                                    </p>
                                ) : (
                                    currentJsonFields.map((field, index) => (
                                        <div
                                            key={field.key}
                                            className="flex items-center space-x-2"
                                        >
                                            <input
                                                type="text"
                                                value={field.key}
                                                onChange={(e) => {
                                                    const newFields = [
                                                        ...currentJsonFields,
                                                    ];
                                                    newFields[index] = {
                                                        ...field,
                                                        key: e.target.value,
                                                    };
                                                    setJsonFields((prev) => ({
                                                        ...prev,
                                                        [setting.id]: newFields,
                                                    }));
                                                }}
                                                className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900"
                                                placeholder={t(
                                                    "json_editor.field_key"
                                                )}
                                            />

                                            <select
                                                value={field.type}
                                                onChange={(e) => {
                                                    const newFields = [
                                                        ...currentJsonFields,
                                                    ];
                                                    newFields[index] = {
                                                        ...field,
                                                        type: e.target
                                                            .value as any,
                                                    };
                                                    setJsonFields((prev) => ({
                                                        ...prev,
                                                        [setting.id]: newFields,
                                                    }));
                                                }}
                                                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900"
                                            >
                                                <option value="string">
                                                    {t("types.string")}
                                                </option>
                                                <option value="number">
                                                    {t("types.int")}
                                                </option>
                                                <option value="boolean">
                                                    {t("types.bool")}
                                                </option>
                                            </select>

                                            {field.type === "boolean" ? (
                                                <select
                                                    value={
                                                        field.value
                                                            ? "true"
                                                            : "false"
                                                    }
                                                    onChange={(e) =>
                                                        updateJsonField(
                                                            setting.id,
                                                            field.key,
                                                            e.target.value ===
                                                                "true"
                                                        )
                                                    }
                                                    className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900"
                                                >
                                                    <option value="true">
                                                        {t(
                                                            "boolean_values.true"
                                                        )}
                                                    </option>
                                                    <option value="false">
                                                        {t(
                                                            "boolean_values.false"
                                                        )}
                                                    </option>
                                                </select>
                                            ) : field.type === "number" ? (
                                                <input
                                                    type="number"
                                                    value={field.value}
                                                    onChange={(e) =>
                                                        updateJsonField(
                                                            setting.id,
                                                            field.key,
                                                            Number(
                                                                e.target.value
                                                            )
                                                        )
                                                    }
                                                    className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900"
                                                />
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={field.value}
                                                    onChange={(e) =>
                                                        updateJsonField(
                                                            setting.id,
                                                            field.key,
                                                            e.target.value
                                                        )
                                                    }
                                                    className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900"
                                                    placeholder={t(
                                                        "json_editor.field_value"
                                                    )}
                                                />
                                            )}

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    removeJsonField(
                                                        setting.id,
                                                        field.key
                                                    )
                                                }
                                                className="text-red-600 hover:text-red-800 text-sm"
                                                title={t(
                                                    "json_editor.remove_field"
                                                )}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        ) : (
                            /* Свернутый режим - обычный textarea */
                            <textarea
                                value={
                                    typeof value === "object"
                                        ? JSON.stringify(value, null, 2)
                                        : value
                                }
                                onChange={(e) => {
                                    try {
                                        const parsed = JSON.parse(
                                            e.target.value
                                        );
                                        handleSettingChange(setting.id, parsed);
                                    } catch {
                                        handleSettingChange(
                                            setting.id,
                                            e.target.value
                                        );
                                    }
                                }}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono text-gray-900 dark:text-white bg-white dark:bg-gray-900"
                                placeholder='{"key": "value"}'
                            />
                        )}
                    </div>
                );

            default:
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={(e) =>
                            handleSettingChange(setting.id, e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900"
                    />
                );
        }
    };

    // Если пользователь не администратор, показываем сообщение об ошибке
    if (!isAdmin) {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t("title")}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {t("description")}
                    </p>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-5 w-5 text-red-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                                {t("access_denied")}
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                                {t("access_denied_message")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t("title")}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {t("description")}
                    </p>
                </div>

                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    const settingGroups = getSettingGroups(settings || []);
    const currentGroup = settingGroups.find((g) => g.name === selectedGroup);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t("title")}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {t("description")}
                    </p>
                </div>
                <button
                    onClick={() => setShowAddForm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                    {t("actions.add")}
                </button>
            </div>

            {/* Ошибка */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-5 w-5 text-red-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                                {t("error.title")}
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                                {error}
                            </div>
                            <div className="mt-4">
                                <button
                                    onClick={() => setError("")}
                                    className="bg-red-100 text-red-800 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-200"
                                >
                                    {t("close")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!settings || settings.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                        <div className="text-center">
                            <svg
                                className="mx-auto h-12 w-12 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                                {t("empty.title")}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                {t("empty.message")}
                            </p>
                            <div className="mt-6">
                                <button
                                    onClick={() => setShowAddForm(true)}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    {t("empty.add_first")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Боковая панель с группами */}
                    <div className="lg:col-span-1">
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                                    {t("groups.title")}
                                </h3>
                                {settingGroups.length === 0 ? (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {t("groups.no_groups")}
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {settingGroups.map((group) => (
                                            <button
                                                key={group.name}
                                                onClick={() =>
                                                    setSelectedGroup(group.name)
                                                }
                                                className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                                                    selectedGroup === group.name
                                                        ? "bg-blue-100 text-blue-700"
                                                        : "text-gray-600 hover:bg-gray-100"
                                                }`}
                                            >
                                                {group.name} (
                                                {group.settings.length})
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Основная панель с настройками */}
                    <div className="lg:col-span-3">
                        {currentGroup ? (
                            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                                <div className="px-4 py-5 sm:p-6">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                                        {t("groups.title")}: {currentGroup.name}
                                    </h3>

                                    <div className="space-y-6">
                                        {currentGroup.settings.map(
                                            (setting) => (
                                                <div
                                                    key={setting.id}
                                                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                                                >
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {setting.key}
                                                            </h4>
                                                            {setting.description && (
                                                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                                    {
                                                                        setting.description
                                                                    }
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <span
                                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                    setting.type ===
                                                                    "string"
                                                                        ? "bg-blue-100 text-blue-800"
                                                                        : setting.type ===
                                                                          "int"
                                                                        ? "bg-green-100 text-green-800"
                                                                        : setting.type ===
                                                                          "bool"
                                                                        ? "bg-yellow-100 text-yellow-800"
                                                                        : "bg-purple-100 text-purple-800"
                                                                }`}
                                                            >
                                                                {setting.type}
                                                            </span>
                                                            <button
                                                                onClick={() =>
                                                                    deleteSettingHandler(
                                                                        setting
                                                                    )
                                                                }
                                                                className="text-red-600 hover:text-red-900 text-sm"
                                                            >
                                                                {t(
                                                                    "actions.delete"
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-3">
                                                        {renderSettingValue(
                                                            setting
                                                        )}

                                                        {(editingSettings[
                                                            setting.id
                                                        ] !== undefined ||
                                                            jsonFields[
                                                                setting.id
                                                            ]) && (
                                                            <div className="flex space-x-2">
                                                                <button
                                                                    onClick={() =>
                                                                        saveSetting(
                                                                            setting
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        saving
                                                                    }
                                                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium disabled:opacity-50"
                                                                >
                                                                    {saving
                                                                        ? t(
                                                                              "actions.saving"
                                                                          )
                                                                        : t(
                                                                              "actions.save"
                                                                          )}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingSettings(
                                                                            (
                                                                                prev
                                                                            ) => {
                                                                                const newState =
                                                                                    {
                                                                                        ...prev,
                                                                                    };
                                                                                delete newState[
                                                                                    setting
                                                                                        .id
                                                                                ];
                                                                                return newState;
                                                                            }
                                                                        );
                                                                        setJsonFields(
                                                                            (
                                                                                prev
                                                                            ) => {
                                                                                const newState =
                                                                                    {
                                                                                        ...prev,
                                                                                    };
                                                                                delete newState[
                                                                                    setting
                                                                                        .id
                                                                                ];
                                                                                return newState;
                                                                            }
                                                                        );
                                                                        setExpandedJsonSettings(
                                                                            (
                                                                                prev
                                                                            ) => {
                                                                                const newState =
                                                                                    {
                                                                                        ...prev,
                                                                                    };
                                                                                delete newState[
                                                                                    setting
                                                                                        .id
                                                                                ];
                                                                                return newState;
                                                                            }
                                                                        );
                                                                    }}
                                                                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 dark:text-gray-300 dark:bg-gray-900 dark:hover:bg-gray-800 px-3 py-1 rounded text-sm font-medium"
                                                                >
                                                                    {t(
                                                                        "actions.cancel"
                                                                    )}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : settingGroups.length === 0 ? (
                            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                                <div className="px-4 py-5 sm:p-6">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        {t("groups.no_groups")}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                                <div className="px-4 py-5 sm:p-6">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        {t("groups.select_group")}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Модальное окно для добавления новой настройки */}
            {showAddForm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                                {t("form.title")}
                            </h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t("form.key")}
                                    </label>
                                    <input
                                        type="text"
                                        value={newSetting.key}
                                        onChange={(e) =>
                                            setNewSetting((prev) => ({
                                                ...prev,
                                                key: e.target.value,
                                            }))
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white"
                                        placeholder={t("form.key_placeholder")}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t("form.type")}
                                    </label>
                                    <select
                                        value={newSetting.type}
                                        onChange={(e) =>
                                            setNewSetting((prev) => ({
                                                ...prev,
                                                type: e.target.value as any,
                                            }))
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white"
                                    >
                                        <option value="string">
                                            {t("types.string")}
                                        </option>
                                        <option value="int">
                                            {t("types.int")}
                                        </option>
                                        <option value="bool">
                                            {t("types.bool")}
                                        </option>
                                        <option value="json">
                                            {t("types.json")}
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t("form.value")}
                                    </label>
                                    {newSetting.type === "bool" ? (
                                        <select
                                            value={newSetting.value}
                                            onChange={(e) =>
                                                setNewSetting((prev) => ({
                                                    ...prev,
                                                    value: e.target.value,
                                                }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white"
                                        >
                                            <option value="true">
                                                {t("boolean_values.true")}
                                            </option>
                                            <option value="false">
                                                {t("boolean_values.false")}
                                            </option>
                                        </select>
                                    ) : newSetting.type === "json" ? (
                                        <textarea
                                            value={newSetting.value}
                                            onChange={(e) =>
                                                setNewSetting((prev) => ({
                                                    ...prev,
                                                    value: e.target.value,
                                                }))
                                            }
                                            rows={4}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono text-gray-900 dark:text-white bg-white"
                                            placeholder='{"key": "value"}'
                                        />
                                    ) : (
                                        <input
                                            type={
                                                newSetting.type === "int"
                                                    ? "number"
                                                    : "text"
                                            }
                                            value={newSetting.value}
                                            onChange={(e) =>
                                                setNewSetting((prev) => ({
                                                    ...prev,
                                                    value: e.target.value,
                                                }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white"
                                        />
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        {t("form.description")}
                                    </label>
                                    <input
                                        type="text"
                                        value={newSetting.description}
                                        onChange={(e) =>
                                            setNewSetting((prev) => ({
                                                ...prev,
                                                description: e.target.value,
                                            }))
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-white bg-white"
                                        placeholder={t(
                                            "form.description_placeholder"
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 mt-6">
                                <button
                                    onClick={() => setShowAddForm(false)}
                                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md text-sm font-medium"
                                >
                                    {t("actions.cancel")}
                                </button>
                                <button
                                    onClick={addNewSetting}
                                    disabled={saving || !newSetting.key}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                                >
                                    {saving ? t("form.adding") : t("form.add")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsPage;
